import {
  CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangeStatusLegacy,
  CohortDslLegacy,
  DSLValueLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  SubjectVariableLegacy,
} from '@verantos-dev/types-cohorts';
import {
  getFullDslQueryString,
  transformSubjectVariableToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

import { computeDiffForCohortBuilderItem } from '../computeDiffForCohortBuilderItem';

export const getFullCohortDslForSubjectsVariables = (
  currentSubjectVariables: SubjectVariableLegacy[],
  nextSubjectVariable: SubjectVariableLegacy,
  currentCohortDsl?: CohortDslLegacy
) => {
  const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
    (subjectVariable) => subjectVariable.id === nextSubjectVariable.id
  );

  const nextSubjectVariables =
    payloadSubjectVariableIndex > -1
      ? currentSubjectVariables.map((subjectVariable) => {
          if (subjectVariable.id === nextSubjectVariable.id) {
            let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
            if (computeDiffForCohortBuilderItem(subjectVariable, nextSubjectVariable)) {
              // if subject variable previously existed and is different from current update
              if (subjectVariable.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                // if subject variable was new, then it's still new
                _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
              } else {
                // subject variable is edited
                _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
              }
            } else {
              // if subject variable previously existed and is the same as current update, then it's unchanged
              _changeStatus =
                subjectVariable.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
            }

            return {
              ...nextSubjectVariable,
              changeStatus: _changeStatus,
            };
          }
          return subjectVariable;
        })
      : [
          ...currentSubjectVariables,
          { ...nextSubjectVariable, changeStatus: CohortBuilderItemChangeStatusLegacy.NEW },
        ];

  const newSubjectVariableDSL = transformSubjectVariableToDsl(nextSubjectVariable);
  const subjectVariableStrings = currentCohortDsl?.subjectVariables ?? [];
  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria:
      nextSubjectVariables.reduce(
        (nextInclusionCriteria: DSLValueLegacy[], nextSubjectVariable) => {
          if (
            nextSubjectVariable.criteriaSelection ===
            SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
          ) {
            nextInclusionCriteria.push({
              id: nextSubjectVariable.id,
              value: nextSubjectVariable.name,
            });
          }
          return nextInclusionCriteria;
        },
        []
      ) ?? [],
    exclusionCriteria:
      nextSubjectVariables.reduce(
        (nextInclusionCriteria: DSLValueLegacy[], nextSubjectVariable) => {
          if (
            nextSubjectVariable.criteriaSelection ===
            SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION
          ) {
            nextInclusionCriteria.push({
              id: nextSubjectVariable.id,
              value: nextSubjectVariable.name,
            });
          }
          return nextInclusionCriteria;
        },
        []
      ) ?? [],
    studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
    indexDate: currentCohortDsl?.indexDate ?? undefined,
    subjectVariables:
      payloadSubjectVariableIndex > -1
        ? subjectVariableStrings.map((subjectVariableString, i) => {
            if (i === payloadSubjectVariableIndex) {
              return newSubjectVariableDSL;
            }
            return subjectVariableString;
          })
        : [...subjectVariableStrings, newSubjectVariableDSL],
    simpleEvents: currentCohortDsl?.simpleEvents ?? [],
    derivedEvents: currentCohortDsl?.derivedEvents ?? [],
  };

  return getFullDslQueryString(newCohortDsl);
};

export const getFullCohortDslForDeleteSubjectsVariables = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  id: string
) => {
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const currentCohortDsl = currentCohortDefinition?.cohortDsl;
  const currentSubjectVariablesDsl = currentCohortDsl?.subjectVariables ?? [];
  const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
    (subjectVariable) => subjectVariable.id === id
  );

  const nextSubjectVariables = [
    ...currentSubjectVariables.slice(0, payloadSubjectVariableIndex),
    ...currentSubjectVariables.slice(payloadSubjectVariableIndex + 1),
  ];

  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria:
      nextSubjectVariables.reduce((nextInclusionCriteria: DSLValueLegacy[], subjectVariable) => {
        if (
          subjectVariable.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
        ) {
          nextInclusionCriteria.push({ id: subjectVariable.id, value: subjectVariable.name });
        }
        return nextInclusionCriteria;
      }, []) ?? [],
    exclusionCriteria:
      nextSubjectVariables.reduce((nextInclusionCriteria: DSLValueLegacy[], subjectVariable) => {
        if (
          subjectVariable.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION
        ) {
          nextInclusionCriteria.push({ id: subjectVariable.id, value: subjectVariable.name });
        }
        return nextInclusionCriteria;
      }, []) ?? [],
    studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
    indexDate: currentCohortDsl?.indexDate ?? undefined,
    subjectVariables: [
      ...currentSubjectVariablesDsl.slice(0, payloadSubjectVariableIndex),
      ...currentSubjectVariablesDsl.slice(payloadSubjectVariableIndex + 1),
    ],
    simpleEvents: currentCohortDsl?.simpleEvents ?? [],
    derivedEvents: currentCohortDsl?.derivedEvents ?? [],
  };

  return { newCohortDsl, fullDslQueryString: getFullDslQueryString(newCohortDsl) };
};
